import {
  ExtendedShareholderDTO,
  totalNumberOfShares,
  totalNumberOfSharesOfShareholder,
} from "@/types/Shareholder";
import { ApiService, ShareholderContributionDetailsResponse } from "@/api";
import { makeAutoObservable } from "mobx";
import AlertMessageStore from "@/stores/AlertMessageStore";
import { combineDateAndTime, toDate } from "@/utils/Dates";
import { History } from "history";
import {
  adminShareholdersNewShareholderContribution,
  navigateToShareholderContributionDetails,
} from "@/utils/navigation";
import { failure, isOk, ok, pending, Result } from "@/common/common";
import { compact, groupBy, identity, maxBy, sortBy, sum } from "lodash";
import { TMessageKey } from "@/i18n/types.gen";
import { Language } from "@/i18n/languages";
import { formatDate } from "@/i18n/dateFormat";
import { FileUploadConfig, toFormData, validateFiles } from "@/fileUpload/fileUploadDomain";
import { TFunction, TMessage } from "@/i18n/tt";
import {
  LocalDateFieldModel,
  NumberFieldModel,
  RadioFieldModel,
  StringFieldModel,
} from "@/common/commonForms";
import {
  ShareholderContribution,
  ShareholderContributionDetails,
  ShareholderContributionType,
  shareholderContributionTypes,
} from "@/shareholderRegister/shareholderRegisterDomain";
import { ShareTypeCode } from "@/common/share";
import { percentOf } from "@/utils/numbers";
import { DataTableProps } from "@/components-v2/DataTable";
import { EmptyStateProps } from "@/components-v2/EmptyState";
import { isSwedishOrgNo } from "@/utils/orgNo";
import { navigate } from "@/events/eventsDomain";

export class ShareholderContributionModel {
  singleContributingShareholder: RadioFieldModel<ExtendedShareholderDTO | null>;
  multipleContributingShareholders: ContributionPerShareholderModel[];
  amount: NumberFieldModel;
  registrationDate: LocalDateFieldModel;
  recallableAmount: NumberFieldModel;
  comment: StringFieldModel | null = null;
  formMode = new RadioFieldModel<FormMode>({
    value: "single",
    options: [...formModes],
    idFn: identity,
    labelFn: formModeLabel,
  });
  error?: TMessage;

  constructor(
    readonly apiService: ApiService,
    readonly messageStore: AlertMessageStore,
    readonly organisationNumber: string,
    readonly currency: string,
    readonly history: History,
    readonly shareholders: ExtendedShareholderDTO[],
    readonly contributionType: ShareholderContributionType,
  ) {
    this.singleContributingShareholder = new RadioFieldModel({
      value: shareholders.length > 0 ? shareholders[0] : null,
      options: shareholders,
      idFn: (s) => String(s?.id),
      labelFn: (s) => ({ translated: s?.name ?? "" }),
      required: true,
    });
    this.multipleContributingShareholders = sortBy(shareholders, "name").map(
      (s) => new ContributionPerShareholderModel(s),
    );
    this.amount = new NumberFieldModel({ required: true, isPositiveOrZero: true });
    this.recallableAmount = new NumberFieldModel({
      required: true,
      value: 0,
      isPositiveOrZero: true,
    });
    this.registrationDate = new LocalDateFieldModel({ required: true });
    makeAutoObservable(this, { apiService: false }, { autoBind: true });
  }

  get isSingleShareholderMode() {
    return this.formMode.value === "single";
  }

  addComment() {
    this.comment = new StringFieldModel();
  }

  validate() {
    this.error = undefined;
    if (this.isSingleShareholderMode) {
      this.singleContributingShareholder.validate();
      this.amount.validate();
      this.recallableAmount.validate();
    } else {
      this.multipleContributingShareholders.forEach((s) => s.validate());
      if (this.multipleContributingShareholders.filter((s) => s.amount.parsedValue).length === 0) {
        this.error = { key: "shareholder-contributions.error.no-amount" };
      }
    }
    this.registrationDate.validate();

    return (
      (this.isSingleShareholderMode
        ? this.singleContributingShareholder.isValid &&
          this.amount.isValid &&
          this.recallableAmount.isValid
        : this.multipleContributingShareholders.every((s) => s.isValid) &&
          this.multipleContributingShareholders.filter((s) => s.amount.parsedValue).length > 0) &&
      this.registrationDate.isValid
    );
  }

  *submit() {
    if (!this.validate()) return;

    try {
      const contributionId: number = yield this.apiService.registerShareholderContribution({
        shareholderContributionId: -1,
        organisationNumber: this.organisationNumber,
        type: this.contributionType,
        registrationDate: combineDateAndTime(this.registrationDate.validatedValue!, "12:00"),
        comment: this.comment?.validatedValue ?? null,
        details: this.isSingleShareholderMode
          ? [
              {
                shareholderContributionDetailsId: -1,
                shareholderContributionId: -1,
                shareholderId: this.singleContributingShareholder.value!.id,
                currency: this.currency,
                recallableAmount: this.recallableAmount.parsedValue!,
                amount: this.amount.parsedValue!,
              },
            ]
          : this.multipleContributingShareholders
              .filter((s) => s.amount.parsedValue)
              .map((s) => ({
                shareholderContributionDetailsId: -1,
                shareholderContributionId: -1,
                shareholderId: s.shareholder.id,
                currency: this.currency,
                recallableAmount: s.recallableAmount.parsedValue!,
                amount: s.amount.parsedValue!,
              })),
      });

      navigateToShareholderContributionDetails(
        this.organisationNumber,
        contributionId,
        this.history,
      );
      this.messageStore.showSuccess({ key: "shareholder-contributions.register-success" });
    } catch (e) {
      this.messageStore.showError({ key: "shareholder-contributions.register-error" });
    }
  }
}

class ContributionPerShareholderModel {
  amount = new NumberFieldModel({ isPositiveOrZero: true });
  recallableAmount = new NumberFieldModel({ required: true, value: 0, isPositiveOrZero: true });

  constructor(readonly shareholder: ExtendedShareholderDTO) {
    makeAutoObservable(this, {}, { autoBind: true });
  }

  validate() {
    this.amount.validate();
    this.recallableAmount.validate();
  }

  get isValid() {
    return this.amount.isValid && this.recallableAmount.isValid;
  }
}

const formModes = ["single", "multiple"] as const;

type FormMode = (typeof formModes)[number];

function formModeLabel(mode: FormMode): TMessage {
  switch (mode) {
    case "single":
      return { key: "shareholder-contributions.form-mode.single" };
    case "multiple":
      return { key: "shareholder-contributions.form-mode.multiple" };
  }
}

export function parseContributionType(value: string): ShareholderContributionType {
  const type = shareholderContributionTypes.find((type) => type === value.toUpperCase());
  return type ?? "COMMITMENT";
}

export type ContributionRow = {
  id: number;
  type: ShareholderContributionType;
  description: string;
  registrationDate: string;
  updatedDate: string;
};

export class ShareholderContributionController {
  fetchShareholderContributionsResult?: Result<ShareholderContribution[]>;
  fetchSingleShareholderContributionResult?: Result<ShareholderContributionDetailsResponse>;
  textFilter: StringFieldModel = new StringFieldModel();
  typeFilter: RadioFieldModel<ShareholderContributionType | null> = new RadioFieldModel({
    value: null,
    options: [null, ...shareholderContributionTypes],
    idFn: (s) => s ?? "ALL",
    labelFn: (s) => ({
      key: s ? shareholderContributionTypeLabel(s) : "shareholder-contributions.type.all",
    }),
  });
  changeDateForContributionId?: number;
  changeDateModel: LocalDateFieldModel = new LocalDateFieldModel();
  submitChangeDateResult?: Result<TMessage, TMessage>;
  deleteContributionId?: number;
  submitDeleteResult?: Result<TMessage, TMessage>;

  constructor(
    readonly apiService: ApiService,
    readonly messageStore: AlertMessageStore,
    readonly organisationNumber: string,
    contributions?: ShareholderContribution[],
  ) {
    makeAutoObservable(this, { apiService: false }, { autoBind: true });
    if (contributions) {
      this.fetchShareholderContributionsResult = ok(contributions);
    }
  }

  *fetchShareholderContributions() {
    this.fetchShareholderContributionsResult = pending();
    try {
      const contributions: ShareholderContribution[] =
        yield this.apiService.fetchShareholderContributions(this.organisationNumber);
      this.fetchShareholderContributionsResult = ok(contributions);
    } catch (e) {
      this.fetchShareholderContributionsResult = failure();
    }
  }

  *fetchSingleShareholderContribution(contributionId: number) {
    this.fetchSingleShareholderContributionResult = pending();
    try {
      const response: ShareholderContributionDetailsResponse =
        yield this.apiService.fetchSingleShareholderContributions(
          this.organisationNumber,
          contributionId,
        );
      this.fetchSingleShareholderContributionResult = ok(response);
    } catch (e) {
      this.fetchSingleShareholderContributionResult = failure();
    }
  }

  findShareholderData(
    shareholder: ExtendedShareholderDTO | null,
    shares: {
      [shareTypeCode in ShareTypeCode]?: number;
    },
  ) {
    if (isOk(this.fetchShareholderContributionsResult) && shareholder) {
      return prepareShareholderData(
        this.fetchShareholderContributionsResult.data,
        shareholder,
        totalNumberOfShares(shares),
      );
    }
  }

  *handleUpload(
    files: File[],
    contribution: ShareholderContribution,
    fileUploadConfig: FileUploadConfig,
  ) {
    yield validateFiles(files, fileUploadConfig);
    yield this.apiService.uploadContributionAttachments(
      this.organisationNumber,
      contribution.reference!,
      toFormData(files),
    );

    this.fetchSingleShareholderContribution(contribution.shareholderContributionId);
  }

  downloadAttachment(reference: string, attachmentId: string) {
    this.apiService.downloadContributionAttachment(
      this.organisationNumber,
      reference,
      attachmentId,
    );
  }

  *deleteAttachment(contribution: ShareholderContribution, attachmentId: string) {
    yield this.apiService.deleteContributionAttachment(
      this.organisationNumber,
      contribution.reference!,
      attachmentId,
    );
    this.fetchSingleShareholderContribution(contribution.shareholderContributionId);
  }

  showChangeDateDialogForContribution(contributionId: number) {
    this.changeDateForContributionId = contributionId;
    this.changeDateModel = new LocalDateFieldModel();
  }

  get isShowingChangeDateDialog() {
    return this.changeDateForContributionId !== undefined;
  }

  hideChangeDateDialog() {
    this.changeDateForContributionId = undefined;
    this.submitChangeDateResult = undefined;
  }

  *submitChangeDate() {
    this.submitChangeDateResult = pending();
    try {
      yield this.apiService.changeContributionDate(
        this.organisationNumber,
        this.changeDateForContributionId!,
        combineDateAndTime(this.changeDateModel.validatedValue!, "12:00"),
      );

      this.submitChangeDateResult = ok({ key: "ok" });
      this.hideChangeDateDialog();
      this.fetchShareholderContributions();
    } catch (e) {
      this.submitChangeDateResult = failure({ key: "shareholder-contributions.change-date-error" });
    }
  }

  showDeleteConfirmationDialogForContribution(contributionId: number) {
    this.deleteContributionId = contributionId;
  }

  get isShowingDeleteConfirmationDialog() {
    return this.deleteContributionId !== undefined;
  }

  hideDeleteConfirmationDialog() {
    this.deleteContributionId = undefined;
    this.submitDeleteResult = undefined;
  }

  *submitDelete() {
    this.submitDeleteResult = pending();
    try {
      yield this.apiService.deleteContribution(this.organisationNumber, this.deleteContributionId!);

      this.submitDeleteResult = ok({ key: "shareholder-contributions.delete-success" });
      this.hideChangeDateDialog();
      this.fetchShareholderContributions();
    } catch (e) {
      this.submitDeleteResult = failure({ key: "shareholder-contributions.delete-error" });
    }
  }
}

export function prepareContributionRows(
  language: Language,
  contributions: ShareholderContribution[],
  shareholders: ExtendedShareholderDTO[],
  t: TFunction,
): ContributionRow[] {
  const shareholderMap = groupBy(shareholders, "id");
  return contributions.map((contribution) => {
    return {
      id: contribution.shareholderContributionId,
      type: contribution.type,
      description: contribution.details
        .map((detail) =>
          t({
            key: contributionDescriptionLabel(contribution),
            params: {
              name: shareholderMap[detail.shareholderId]?.[0]?.name,
              amount: detail.amount,
              currency: detail.currency,
            },
          }),
        )
        .join(", "),
      registrationDate: formatDate(language, contribution.registrationDate),
      updatedDate: contribution.updatedAt ? formatDate(language, contribution.updatedAt) : "",
    };
  });
}

export function shareholderContributionTypeLabel(
  contributionType: ShareholderContributionType,
): TMessageKey {
  switch (contributionType) {
    case "CONTRIBUTION":
      return "shareholder-contributions.type.CONTRIBUTION";
    case "COMMITMENT":
      return "shareholder-contributions.type.COMMITMENT";
    case "DISTRIBUTION":
      return "shareholder-contributions.type.DISTRIBUTION";
  }
}

export function contributionRegisterLabel(
  contributionType: ShareholderContributionType,
): TMessageKey {
  switch (contributionType) {
    case "CONTRIBUTION":
      return "shareholder-contributions.register.CONTRIBUTION";
    case "COMMITMENT":
      return "shareholder-contributions.register.COMMITMENT";
    case "DISTRIBUTION":
      return "shareholder-contributions.register.DISTRIBUTION";
  }
}

export function filterContributionRows(
  rows: ContributionRow[],
  filter: string,
  contributionType: ShareholderContributionType | null,
): ContributionRow[] {
  return rows.filter(
    (row) =>
      Object.values(row).some(
        (value) => typeof value === "string" && value.toLowerCase().includes(filter),
      ) &&
      (contributionType === null || contributionType === row.type),
  );
}

export type ShareholderContributionSubpage = "overview" | "log";

export type ShareholderContributionData = {
  id: number;
  shareholder: string;
  ownership: number;
  committed: number;
  contributed: number;
  distributed: number;
  recallable: number;
  net: number;
  remaining: number;
};

export function prepareOverviewRows(
  contributions: ShareholderContribution[],
  shareholders: ExtendedShareholderDTO[],
  sharesTotal: number,
): ShareholderContributionData[] {
  const contributionsByType = groupBy(contributions, "type");
  const commitmentDetailsMap = detailsByShareholder(contributionsByType["COMMITMENT"]);
  const contributionDetailsMap = detailsByShareholder(contributionsByType["CONTRIBUTION"]);
  const distributionDetailsMap = detailsByShareholder(contributionsByType["DISTRIBUTION"]);
  return shareholders
    .filter(
      (s) =>
        commitmentDetailsMap[s.id] || contributionDetailsMap[s.id] || distributionDetailsMap[s.id],
    )
    .map((s) =>
      calculateShareholderContributionData(
        commitmentDetailsMap[s.id],
        contributionDetailsMap[s.id],
        distributionDetailsMap[s.id],
        s,
        sharesTotal,
      ),
    );
}

export type ShareholderContributionTotalData = {
  committed: number;
  contributed: number;
  distributed: number;
  recallable: number;
  net: number;
  remaining: number;
  remainingPercent: number;
  lastRegistrationDate?: string;
};

export function prepareContributionOverviewTotal(
  contributions: ShareholderContribution[],
  data: ShareholderContributionData[],
): ShareholderContributionTotalData {
  const committed = data.reduce((sum, d) => sum + d.committed, 0);
  const remaining = data.reduce((sum, d) => sum + d.remaining, 0);
  return {
    committed,
    contributed: data.reduce((sum, d) => sum + d.contributed, 0),
    distributed: data.reduce((sum, d) => sum + d.distributed, 0),
    recallable: data.reduce((sum, d) => sum + d.recallable, 0),
    net: data.reduce((sum, d) => sum + d.net, 0),
    remaining,
    remainingPercent: percentOf(remaining, committed),
    lastRegistrationDate: maxBy(contributions, (x) => toDate(x.registrationDate))?.registrationDate,
  };
}

export function prepareShareholderData(
  contributions: ShareholderContribution[],
  shareholder: ExtendedShareholderDTO,
  sharesTotal: number,
): ShareholderContributionData {
  const contributionsByType = groupBy(contributions, "type");
  const commitmentDetailsMap = detailsByShareholder(contributionsByType["COMMITMENT"]);
  const contributionDetailsMap = detailsByShareholder(contributionsByType["CONTRIBUTION"]);
  const distributionDetailsMap = detailsByShareholder(contributionsByType["DISTRIBUTION"]);
  return calculateShareholderContributionData(
    commitmentDetailsMap[shareholder.id],
    contributionDetailsMap[shareholder.id],
    distributionDetailsMap[shareholder.id],
    shareholder,
    sharesTotal,
  );
}

function detailsByShareholder(contributions?: ShareholderContribution[]) {
  if (!contributions) return {};
  const detailsForType = contributions.flatMap((c) => c.details);
  return groupBy(detailsForType, "shareholderId");
}

export function calculateShareholderContributionData(
  commitmentDetails: ShareholderContributionDetails[] | undefined,
  contributionDetails: ShareholderContributionDetails[] | undefined,
  distributionDetails: ShareholderContributionDetails[] | undefined,
  shareholder: ExtendedShareholderDTO,
  sharesTotal: number,
): ShareholderContributionData {
  const committed = sumContributionAmount(commitmentDetails);
  const contributed = sumContributionAmount(contributionDetails);
  const distributed = sumContributionAmount(distributionDetails);
  const recallable = distributionDetails
    ? sum(distributionDetails.map((c) => c.recallableAmount))
    : 0;
  const remaining = committed - contributed + recallable;
  return {
    id: shareholder.id,
    shareholder: shareholder.name,
    ownership: totalNumberOfSharesOfShareholder(shareholder) / sharesTotal,
    committed: committed,
    contributed,
    distributed,
    recallable,
    net: contributed - distributed,
    remaining: remaining > 0 ? remaining : 0,
  };
}

export function sumContributionAmount(details?: ShareholderContributionDetails[]) {
  return details ? details.map((c) => c.amount).reduce((a, b) => a + b, 0) : 0;
}

export function contributionDescriptionLabel(contribution: ShareholderContribution): TMessageKey {
  switch (contribution.type) {
    case "CONTRIBUTION":
      return "shareholder-contributions.description.CONTRIBUTION";
    case "COMMITMENT":
      return "shareholder-contributions.description.COMMITMENT";
    case "DISTRIBUTION":
      return "shareholder-contributions.description.DISTRIBUTION";
  }
}

export const prepareContributionsTable = ({
  data,
  showChangeDateDialogForContribution,
  showDeleteConfirmationDialogForContribution,
  navigateToContributionDetails,
}: {
  data: ContributionRow[];
  showChangeDateDialogForContribution?: (contributionId: number) => void;
  showDeleteConfirmationDialogForContribution?: (contributionId: number) => void;
  navigateToContributionDetails?: (contributionId: number) => void;
}): DataTableProps<ContributionRow> => {
  return {
    tableData: data,
    tableColumns: [
      {
        name: "type",
        label: { key: "shareholder-contributions.type" },
        align: "left",
        getValue: (row) => ({ key: shareholderContributionTypeLabel(row.type) }),
      },
      {
        name: "description",
        label: { key: "shareholder-contributions.description" },
        align: "left",
      },
      {
        name: "registrationDate",
        label: { key: "shareholder-contributions.registered" },
        align: "left",
      },
      {
        name: "updatedDate",
        label: { key: "shareholder-contributions.lastUpdated" },
        align: "left",
      },
    ],
    rowActions:
      showChangeDateDialogForContribution || showDeleteConfirmationDialogForContribution
        ? (row) => [
            [
              "context-menu",
              compact([
                showChangeDateDialogForContribution && {
                  type: "item",
                  label: { key: "shareholder-contributions.change-date" },
                  onSelect: () => showChangeDateDialogForContribution(row.id),
                },
                showDeleteConfirmationDialogForContribution && {
                  type: "item",
                  icon: "trash",
                  color: "error",
                  label: { key: "shareholder-contributions.delete" },
                  onSelect: () => showDeleteConfirmationDialogForContribution(row.id),
                },
              ]),
            ],
          ]
        : undefined,
    getId: (row) => row.id,
    onRowClick: navigateToContributionDetails
      ? (row) => navigateToContributionDetails(row.id)
      : undefined,
  };
};

export const prepareContributionsEmptyState = ({ orgNo }: { orgNo: string }): EmptyStateProps => ({
  title: { key: "shareholder-contributions.no-contributions-registered" },
  description: { key: "shareholder-contributions.get-started" },
  illustration: "card-holder",
  actions: [
    {
      type: "button",
      key: "new-commitment",
      props: {
        variant: "secondary",
        label: { key: contributionRegisterLabel("COMMITMENT") },
        icon: "plus",
        onClick: [navigate(adminShareholdersNewShareholderContribution(orgNo, "COMMITMENT"))],
      },
    },
    {
      type: "button",
      key: "new-contribution",
      props: {
        variant: "secondary",
        label: { key: contributionRegisterLabel("CONTRIBUTION") },
        icon: "plus",
        onClick: [navigate(adminShareholdersNewShareholderContribution(orgNo, "CONTRIBUTION"))],
      },
    },
  ],
});

export const isContributionsEnabled = (orgNo: string) => isSwedishOrgNo(orgNo);
